import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export type ReminderAction = {
  label: string;
  /** 不传则按钮只关闭弹窗（用于「确认/知道了」）。 */
  onClick?: () => void;
  variant?: 'default' | 'outline';
};

type ReminderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  actions: ReminderAction[];
};

/**
 * 节点生成相关提醒弹窗的统一外壳，采用工具栏一致的 fm-toolbar-surface 风格。
 * 仅负责样式与布局；具体文案与按钮由调用方通过 props 传入。
 * 点击任意按钮都会先关闭弹窗，再执行其 onClick。
 */
export function ReminderDialog({ open, onOpenChange, icon, title, description, actions }: ReminderDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-sidebar/95 backdrop-blur-2xl border border-sidebar-border shadow-none rounded-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2 bg-sidebar/40 backdrop-blur-2xl text-left">
          <DialogTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground pt-1">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="px-6 pb-6 pt-4 gap-2 sm:gap-2">
          {actions.map((action, i) => (
            <Button
              key={`${action.label}-${i}`}
              variant={action.variant === 'outline' ? 'outline' : undefined}
              className={
                action.variant === 'outline'
                  ? 'rounded-lg h-9 px-4 text-xs font-bold mt-0 hover:bg-sidebar-accent/30 shadow-none'
                  : 'rounded-lg h-9 px-4 text-xs font-bold shadow-none'
              }
              onClick={() => {
                onOpenChange(false);
                action.onClick?.();
              }}
            >
              {action.label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
